<template lang="pug">
    el-drawer.full-height-drawer(
        v-focus-trap="visible"
        :visible="visible"
        :with-header="withHeader"
        :title="title"
        :direction="direction"
        :size="size"
        :wrapper-closable="wrapperClosable"
        :close-on-press-escape="closeOnPressEscape"
        :destroy-on-close="destroyOnClose"
        :append-to-body="appendToBody"
        @update:visible="value => $emit('update:visible', value)"
        @open="$emit('open')"
        @opened="$emit('opened')"
        @close="$emit('close')"
        @closed="$emit('closed')"
    )
        //- A header that needs more than its words (a back button, an icon)
        //- fills el-drawer's own title slot; without it the plain title stays.
        template(v-if="$slots.title" slot="title")
            slot(name="title")
        .drawer-body.no-tabs
            .fhd-content
                slot
        .drawer-footer(v-if="hasFooter")
            .left
                slot(name="footer-left")
            .right.flex-row
                slot(name="footer")
</template>

<script>
/**
 * The one correct way to build a right-hand drawer in this app. It bakes in the
 * structure the global CSS in assets/sass/globals.sass expects, so callers can
 * never reintroduce the recurring "missing padding / footer in the wrong place"
 * bugs, and it is keyboard-complete (v-focus-trap: the focus moves in on open,
 * Tab stays inside, the focus returns on close):
 *
 *   - `el-drawer.full-height-drawer` (component class, NOT custom-class)
 *   - `.drawer-body.no-tabs` (scroll region) → `.fhd-content` (20px padding)
 *   - `.drawer-footer` → `.left` (secondary action) + `.right.flex-row` (Cancel/Save)
 *
 * Usage:
 *   full-height-drawer(:visible.sync="open" :title="$t('…')" size="520px" @open="reset")
 *       el-form(...) …fields…
 *       template(#footer-left)
 *           el-button(…)  // optional secondary action (e.g. "Test connection")
 *       template(#footer)
 *           el-button(@click="close") {{ $t('Cancel') }}
 *           el-button(type="success" @click="submit") {{ $t('Save') }}
 *
 * Events: update:visible, open, opened, close, closed (el-drawer's own; `closed`
 * fires after the slide-out, the moment to hand keyboard focus back).
 */
export default {
    name: 'FullHeightDrawer',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ''
        },
        size: {
            type: [String, Number],
            default: '520px'
        },
        direction: {
            type: String,
            default: 'rtl'
        },
        withHeader: {
            type: Boolean,
            default: true
        },
        wrapperClosable: {
            type: Boolean,
            default: false
        },
        closeOnPressEscape: {
            type: Boolean,
            default: true
        },
        // Opt-in: render the drawer directly under <body>. Needed whenever the
        // drawer is declared inside an element that creates its own stacking
        // context (a positioned header, a transformed panel): the modal overlay
        // is ALWAYS appended to body, so a drawer left in place loses to it no
        // matter how high its own z-index is, and the page ends up dimmed with
        // the drawer behind the dim.
        appendToBody: {
            type: Boolean,
            default: false
        },
        // Opt-in: destroy the slotted content when the drawer closes, so the next open
        // remounts a fresh form. Use it for drawers whose embedded form must not carry
        // stale state across opens (e.g. vee-validate error flags, cached field values).
        destroyOnClose: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        hasFooter() {
            return !!(this.$slots.footer || this.$slots['footer-left'])
        }
    }
}
</script>

<style lang="sass" scoped>
.fhd-content
    padding: 20px
.drawer-footer
    // The two groups never touch, and a footer with more buttons than the
    // drawer is wide wraps instead of running one group into the other.
    // Here, not on the global .drawer-footer: this drawer's footer is
    // exactly `.left` + `.right`, other footers put bare buttons in it and
    // carry Element's own sibling margins.
    gap: 8px
    flex-wrap: wrap

    // Both groups space their buttons with the gap; Element's own sibling
    // margin (`.el-button + .el-button`, 10px) would stack on top of it.
    .left,
    .right.flex-row
        display: flex
        align-items: center
        gap: 8px

        ::v-deep .el-button + .el-button
            margin-left: 0
</style>
